import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Check, AlertTriangle } from "lucide-react";
import { formatTime } from "@/utils";
import { api } from "@/api";
import { logDiag, rememberTraced, stateSummary } from "@/lib/diagnostics";

const isoToLabel = (iso) => {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
};

/** Overdue Dose Picker — a recurring row with 2+ missed beats opens this.
 *  mode="complete": MULTI-SELECT. Checkboxes only select; nothing is written until COMPLETE
 *  SELECTED, which then completes each chosen SOURCE occurrence independently
 *  (POST /tasks/{id}/occurrences/{iso}/complete) and closes once when every write succeeded.
 *  Failures keep the dialog open with the failed dates marked and still selected.
 *  mode="edit": single pick → `onPick(task, iso)` opens the editor on that source key. */
export const OverdueDosePicker = ({ task, onClose, onPick, onCompleted, mode = "complete" }) => {
    const [selected, setSelected] = useState(() => new Set());
    const [done, setDone] = useState(() => new Set());
    const [failed, setFailed] = useState({});
    const [busy, setBusy] = useState(false);
    const [busyIso, setBusyIso] = useState(null);
    if (!task) return null;
    const missed = task._missedOccurrences || [];
    const remaining = missed.filter((iso) => !done.has(iso));
    const selectedCount = remaining.filter((iso) => selected.has(iso)).length;

    const pickForEdit = async (iso) => {
        setBusyIso(iso);
        try { await onPick?.(task, iso); } finally { setBusyIso(null); onClose?.(); }
    };

    const toggleSelect = (iso) => {
        if (busy || done.has(iso)) return;
        setSelected((prev) => { const n = new Set(prev); n.has(iso) ? n.delete(iso) : n.add(iso); return n; });
    };

    const completeSelected = async () => {
        const targets = remaining.filter((iso) => selected.has(iso));
        if (!targets.length || busy) return;
        setBusy(true);
        const nextFailed = {};
        let latest = null;
        for (const iso of targets) {
            const traceId = api.newTraceId();
            logDiag({ kind: "complete.click", task_id: task.id, iso, view: "dose-picker", trace_id: traceId, message: `"${String(task.text || "").slice(0, 60)}" batch ${targets.length}`, state: stateSummary(latest || task) });
            rememberTraced(task.id, iso, task.text);
            setBusyIso(iso);
            try {
                const upd = await api.completeOccurrence(task.id, iso, traceId);
                logDiag({ kind: "complete.response", task_id: task.id, iso, trace_id: traceId, state: stateSummary(upd), message: upd.completions?.includes(iso) ? "persisted" : "RESPONSE LACKS KEY" });
                latest = upd;
                setDone((prev) => new Set(prev).add(iso));
                setSelected((prev) => { const n = new Set(prev); n.delete(iso); return n; });
            } catch (err) {
                const msg = String(err?.response?.data?.detail || err?.message || "failed").slice(0, 120);
                logDiag({ kind: "complete.error", task_id: task.id, iso, trace_id: traceId, status: err?.response?.status, message: msg });
                nextFailed[iso] = msg;
            }
        }
        setBusyIso(null);
        setBusy(false);
        if (latest) onCompleted?.(latest);
        setFailed(nextFailed);
        if (Object.keys(nextFailed).length === 0) onClose?.();
    };

    const isEdit = mode === "edit";
    return (
        <Dialog open={!!task} onOpenChange={(open) => { if (!open && !busy) onClose?.(); }}>
            <DialogContent className="bg-meye-bg border-meye-border text-zinc-100 max-w-sm" data-testid="overdue-dose-picker">
                <DialogHeader>
                    <DialogTitle className="font-brand tracking-widest uppercase text-sm text-meye-fuchsia">
                        Which dose? · {task.text}
                    </DialogTitle>
                    <DialogDescription className="sr-only">Per-occurrence actions keyed by source date</DialogDescription>
                </DialogHeader>
                <p className="text-xs text-zinc-400 -mt-2" data-testid="overdue-dose-picker-hint">
                    {isEdit
                        ? `${missed.length} outstanding — choose the occurrence you want to edit. Nothing is written until you save.`
                        : `${remaining.length} missed — tick every dose you actually took, then Complete Selected. Only those days are written.`}
                </p>
                {!isEdit && remaining.length > 1 && (
                    <button type="button" onClick={() => setSelected(new Set(remaining))} disabled={busy}
                        className="self-start text-[10px] uppercase tracking-widest text-meye-cyan hover:underline disabled:opacity-40" data-testid="overdue-dose-select-all">
                        select all missed ({remaining.length})
                    </button>
                )}
                <div className="space-y-1 max-h-[50vh] overflow-y-auto pr-1">
                    {missed.map((iso) => {
                        const isDone = done.has(iso);
                        const isSel = selected.has(iso);
                        const err = failed[iso];
                        return (
                            <button
                                key={iso}
                                type="button"
                                onClick={() => (isEdit ? pickForEdit(iso) : toggleSelect(iso))}
                                disabled={busy || isDone}
                                aria-pressed={isEdit ? undefined : isSel}
                                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md border text-left transition-colors ${isDone ? "border-emerald-500/30 bg-emerald-500/10 opacity-70" : err ? "border-amber-400/60 bg-amber-500/10" : isSel ? "border-meye-cyan/60 bg-meye-cyan/15" : "border-red-500/25 bg-red-500/5 hover:bg-meye-cyan/10 hover:border-meye-cyan/40"}`}
                                data-testid={`overdue-dose-${iso}`}
                                data-selected={isSel ? "true" : "false"}
                                data-state={isDone ? "done" : err ? "failed" : isSel ? "selected" : "idle"}
                            >
                                <span className={`w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 ${isSel || isDone ? "border-meye-cyan bg-meye-cyan/20" : "border-zinc-600"}`}>
                                    {busyIso === iso ? <Loader2 size={12} className="animate-spin text-meye-cyan" /> : (isSel || isDone) ? <Check size={12} className="text-meye-cyan" /> : null}
                                </span>
                                <span className="flex-1 text-sm font-medium">{isoToLabel(iso)}</span>
                                {err && <span className="text-[10px] text-amber-300 flex items-center gap-1" data-testid={`overdue-dose-error-${iso}`}><AlertTriangle size={10} /> failed</span>}
                                {task.time && !err && <span className="text-xs text-meye-cyan">{formatTime(task.time)}</span>}
                                <span className="text-[10px] font-mono text-zinc-500">{iso}</span>
                            </button>
                        );
                    })}
                </div>
                {Object.keys(failed).length > 0 && (
                    <p className="text-xs text-amber-300" data-testid="overdue-dose-failure-summary">
                        {Object.keys(failed).length} not saved ({Object.keys(failed).join(", ")}) — still selected, try again or cancel.
                    </p>
                )}
                {!isEdit && (
                    <div className="flex items-center justify-end gap-2 pt-2 border-t border-meye-border" data-testid="overdue-dose-footer">
                        <button type="button" onClick={() => onClose?.()} disabled={busy}
                            className="px-3 py-2 rounded-md text-xs uppercase tracking-widest text-zinc-400 hover:text-zinc-100 disabled:opacity-40" data-testid="overdue-dose-cancel">
                            Cancel
                        </button>
                        <button type="button" onClick={completeSelected} disabled={busy || selectedCount === 0}
                            className="px-4 py-2 rounded-md bg-meye-cyan text-black text-xs font-brand uppercase tracking-widest disabled:opacity-40 flex items-center gap-2" data-testid="overdue-dose-complete-selected">
                            {busy && <Loader2 size={12} className="animate-spin" />}
                            Complete selected ({selectedCount})
                        </button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default OverdueDosePicker;
